import React, { FunctionComponent } from "react";
import { Col, Row } from "react-bootstrap";
import "./dashboard-modern.scss";

import { NumberOfRegistrationsChart } from "./NumberOfRegistrationsChart";
import { RegistrationsPerLimitChart } from "./RegistrationsPerLimitChart";
import { useLimits } from "../../hooks/useLimits";
import { useRegistrations } from "../../hooks/useRegistrations";
import { useAuthentication } from "../../hooks/useAuthentication";
import { useAuthUser } from "../../hooks/useAuthUser";
import { EmailVerifiedNotification } from "../common/EmailVerifiedNotification";
import { ContentLayout } from "../common/Layout";
import { useUserIdParam } from "../../hooks/useUserIdParam";

interface Props {
  isAuthenticated: boolean;
}

const Dashboard: FunctionComponent<Props> = ({ isAuthenticated }) => {
  const userId = useUserIdParam();
  useAuthentication(isAuthenticated);
  const { user } = useAuthUser();
  const [registrations, registrationsLoading, registrationsError] = useRegistrations(userId);
  const [limits, limitsLoading, limitsError] = useLimits(userId);

  const isLoading = registrationsLoading || limitsLoading;
  const isError = registrationsError || limitsError;

  // Calculate statistics
  const totalRegistrations = registrations?.length || 0;
  const totalLimits = limits?.length || 0;
  const totalTaken = limits?.reduce((sum, limit) => sum + (limit.taken || 0), 0) || 0;

  // Count only fully booked limits (taken >= limit capacity)
  const fullyBookedLimits = limits?.filter((limit) => limit.taken >= limit.limit).length || 0;

  // Calculate actual utilization: total registrations vs total capacity
  const totalCapacity = limits?.reduce((sum, limit) => sum + (limit.limit || 0), 0) || 0;
  const utilizationRate = totalCapacity > 0 ? Math.round((totalTaken / totalCapacity) * 100) : 0;

  // Calculate total revenue: sum of (price * taken) for all limits
  const totalRevenue = limits?.reduce((sum, limit) => sum + (limit.price || 0) * (limit.taken || 0), 0) || 0;

  // Format currency in CHF
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(amount);
  };

  if (isError) return <p>Fehler beim Laden der Daten</p>;
  else {
    return (
      <ContentLayout siteTitle={"Spielgruppe Online - Dashboard"} title={"Dashboard"} isLoading={isLoading}>
        <EmailVerifiedNotification className={"mt-5"} user={user} />

        <Row className="mb-4">
          <Col lg={3} md={6} className="mb-3">
            <div className="modern-stat-card primary">
              <div className="stat-content">
                <h3 className="stat-number">{totalRegistrations}</h3>
                <p className="stat-label">Anmeldungen</p>
                <small className="stat-change positive">Gesamt erfasst</small>
              </div>
            </div>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <div className="modern-stat-card primary">
              <div className="stat-content">
                <h3 className="stat-number">{fullyBookedLimits}</h3>
                <p className="stat-label">Ausgebuchte Angebote</p>
                <small className="stat-change neutral">von {totalLimits} gesamt</small>
              </div>
            </div>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <div className="modern-stat-card primary">
              <div className="stat-content">
                <h3 className="stat-number">{utilizationRate}%</h3>
                <p className="stat-label">Auslastung</p>
                <small className="stat-change positive">Performance</small>
              </div>
            </div>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <div className="modern-stat-card primary">
              <div className="stat-content">
                <h3 className="stat-number">{Intl.NumberFormat("de-CH").format(totalRevenue)}</h3>
                <p className="stat-label">Umsatz in CHF</p>
                <small className="stat-change positive">Gesamt</small>
              </div>
            </div>
          </Col>
        </Row>

        {/* Individual Limit Charts */}
        <Row className="mb-4">
          <Col>
            <div className="modern-chart-card">
              <div className="chart-header">
                <h4>🎯 Angebote im Detail</h4>
                <span className="chart-badge">Übersicht</span>
              </div>
              <div className="chart-content">
                <Row>
                  {limits.map((limit) => {
                    // Use the 'taken' property from the limit object
                    const limitRegistrations = limit.taken || 0;
                    const limitRevenue = (limit.price || 0) * limitRegistrations;
                    return (
                      <Col xs={6} sm={6} lg={4} xl={3} className="mb-3" key={limit.property}>
                        <div className="limit-detail-card">
                          <div className="limit-chart">
                            <RegistrationsPerLimitChart key={limit.property} width={120} limit={limit} />
                          </div>
                          <div className="limit-info">
                            <h6 className="limit-title">{limit.property}</h6>
                            <p className="limit-count">{limitRegistrations} Anmeldungen</p>
                            <p className="limit-revenue">{formatCurrency(limitRevenue)}</p>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            </div>
          </Col>
        </Row>

        {/* Registration Trend Chart */}
        <Row className="mb-4">
          <Col>
            <div className="modern-chart-card">
              <div className="chart-header">
                <h4>📈 Anmeldungen Verlauf</h4>
                <span className="chart-badge">Trend</span>
              </div>
              <div className="chart-content">
                <NumberOfRegistrationsChart registrations={registrations} />
              </div>
            </div>
          </Col>
        </Row>
      </ContentLayout>
    );
  }
};

export default Dashboard;
